import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'shared/components/alerts';
import regulaOverrides from './regula-overrides.css?inline';
import { FaceCaptureElement, FaceCaptureEvent } from '../../types';
import { StepAction } from '../../../types';
import { getSelfieStepSettings } from '../../utils/get-selfie-step-settings';

export const useSelfieStep = (onSuccess: (selfieImage: string) => void) => {
  const elementRef = useRef<FaceCaptureElement | null>(null);

  const { t, i18n } = useTranslation('web-common');
  const { alertError } = useAlert();

  const handleFaceCaptureEvent = useCallback(
    (event: FaceCaptureEvent) => {
      const { action, data } = event.detail;

      if (action === StepAction.PROCESS_FINISHED) {
        const selfieImage = data?.status === 1 ? data.response?.capture?.[0] : undefined;
        if (selfieImage) {
          onSuccess(selfieImage);
          return;
        }

        alertError(new Error(t('id-submission.selfie-time.couldnt-capture-selfie.message')));
      }

      if (action === StepAction.CLOSE) {
        alertError(new Error(t('id-submission.selfie-time.capture-canceled.message')));
      }
    },
    [onSuccess, alertError]
  );

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return undefined;
    }

    const language = i18n.language;

    element.settings = {
      ...getSelfieStepSettings(),
      locale: language,
    };

    const applyRegulaOverrides = () => {
      const root = element.shadowRoot;
      if (!root) {
        return;
      }
      const existing = root.getElementById('lw-regula-overrides');
      const style = existing ?? document.createElement('style');
      style.id = 'lw-regula-overrides';
      style.textContent = regulaOverrides;

      if (!existing) {
        root.appendChild(style);
      }
    };

    applyRegulaOverrides();

    element.addEventListener('face-capture', handleFaceCaptureEvent);

    return () => {
      element.removeEventListener('face-capture', handleFaceCaptureEvent);
    };
  }, [handleFaceCaptureEvent]);

  return {
    elementRef,
  };
};
